/**
 * 公共 Token 刷新工具
 * 根据 axiosType 区分不同服务器，判断不同的登录失效码并刷新各自的 token
 */

import { getJavaToken, getYs7Token } from '@/redux/app/appSlice'
import { API_FORMAT_CONFIG, getApiFormat } from './api'

/**
 * Token 刷新阈值（秒）
 * 当 token 剩余时间小于此值时，自动刷新 token
 */
export const REFRESH_THRESHOLD = 30 * 60 // 30 分钟

/**
 * 获取 Redux Store
 */
const getReduxStore = () => {
  try {
    if (typeof window !== 'undefined' && window.__REDUX_STORE__) {
      return window.__REDUX_STORE__
    }
  } catch (e) {
    console.warn('[refreshToken] 获取 Redux store 失败:', e)
  }
  return null
}

/**
 * 根据 axiosType 获取对应的 token 刷新 action
 * @param {string} axiosType - API 类型：'java', 'ys7', 'erpAPI', 'erpIndex', 'default'
 * @returns {Function|null} - 返回对应的 token 刷新 action，如果不支持则返回 null
 */
const getTokenRefreshAction = (axiosType) => {
  const tokenRefreshMap = {
    'java': getJavaToken,
    'ys7': getYs7Token,
    // ERP 和其他类型暂时不支持自动刷新（需要跳转登录）
    // 'erpAPI': null,
    // 'erpIndex': null,
    // 'default': null,
  }
  return tokenRefreshMap[axiosType] || null
}

/**
 * Token 刷新 Promise 缓存（防止并发刷新）
 */
const tokenRefreshPromises = {
  java: null,
  ys7: null,
}

/**
 * 规范化过期时间：统一成"秒级时间戳"
 * @param {number|string|null} value - 过期时间（可能是毫秒时间戳或秒时间戳）
 * @returns {number|null} - 秒级时间戳，如果无效则返回 null
 */
const normalizeExpireTimeSec = (value) => {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return null
  // 认为 >= 1e12 是毫秒时间戳（约 2001 年之后）
  return num >= 1e12 ? Math.floor(num / 1000) : Math.floor(num)
}

/**
 * 根据 axiosType 获取对应的过期时间字段名
 * @param {string} axiosType - API 类型
 * @returns {string} - Redux 中的过期时间字段名
 */
const getExpireTimeField = (axiosType) => {
  const expireTimeFieldMap = {
    'java': 'javaTokenExpireTime',
    'ys7': 'ys7TokenExpireTime',
  }
  return expireTimeFieldMap[axiosType] || null
}

/**
 * 刷新指定类型的 token（带过期时间检查）
 * @param {string} axiosType - API 类型
 * @param {Object} options - 可选参数
 * @param {Object} options.params - 传递给 token 刷新 action 的参数（如 Java token 需要 user.token）
 * @param {number} options.refreshThreshold - 刷新阈值（秒），剩余时间小于此值则刷新，默认使用 REFRESH_THRESHOLD（30分钟）
 * @param {boolean} options.refreshWhenNoExpireTime - 没有过期时间时是否刷新，默认 true
 * @param {boolean} options.forceRefresh - 强制刷新，跳过过期时间检查，默认 false
 * @returns {Promise} - 刷新 token 的 Promise
 */
export const refreshTokenByType = async (axiosType, options = {}) => {
  const { 
    params = {},
    refreshThreshold = REFRESH_THRESHOLD, // 默认使用统一的刷新阈值（30分钟）
    refreshWhenNoExpireTime = true, // 没有过期时间时默认刷新
    forceRefresh = false // 是否强制刷新（跳过过期时间检查）
  } = options

  // 检查是否支持该类型的 token 刷新
  const refreshAction = getTokenRefreshAction(axiosType)
  if (!refreshAction) {
    return Promise.reject(new Error(`不支持的 token 类型: ${axiosType}`))
  }

  // 检查是否正在刷新（防止并发）
  if (tokenRefreshPromises[axiosType]) {
    return tokenRefreshPromises[axiosType]
  }

  const store = getReduxStore()
  if (!store) {
    return Promise.reject(new Error('Redux store 不可用，无法刷新 token'))
  }

  // 如果不是强制刷新，检查过期时间
  if (!forceRefresh) {
    const state = store.getState?.() || {}
    const expireTimeField = getExpireTimeField(axiosType)
    
    if (expireTimeField) {
      const expireTime = state?.app?.[expireTimeField]
      const expireTimeSec = normalizeExpireTimeSec(expireTime)
      
      // 计算当前时间戳（秒）
      const currentTime = Math.floor(Date.now() / 1000)
      // 计算刷新阈值时间点（秒）
      const thresholdTime = currentTime + Number(refreshThreshold || 0)
      
      // 判断是否需要刷新
      // 1. 没有过期时间（且 refreshWhenNoExpireTime 为 true）
      // 2. 已过期
      // 3. 剩余时间小于刷新阈值
      const shouldRefresh =
        (refreshWhenNoExpireTime && !expireTimeSec) ||
        (expireTimeSec !== null && expireTimeSec < currentTime) ||
        (expireTimeSec !== null && expireTimeSec < thresholdTime)
      
      if (!shouldRefresh) {
        // 不需要刷新，返回已存在的 token 信息
        const tokenField = axiosType === 'java' ? 'javaToken' : 'ys7Token'
        const token = state?.app?.[tokenField]
        if (token) {
          console.log(`[refreshToken] ${axiosType} token 未过期，剩余时间充足，跳过刷新`)
          return Promise.resolve({ token, expireTime: expireTimeSec })
        }
      } else {
        console.log(`[refreshToken] ${axiosType} token 需要刷新:`, {
          expireTime: expireTimeSec,
          currentTime,
          thresholdTime,
          expired: expireTimeSec !== null && expireTimeSec < currentTime,
          nearExpiry: expireTimeSec !== null && expireTimeSec < thresholdTime
        })
      }
    }
  }

  // 对于 Java token，需要从 Redux 获取 user.token 作为参数
  let finalParams = params
  if (axiosType === 'java' && !params.token) {
    const state = store.getState?.() || {}
    const userToken = state?.app?.user?.token
    if (userToken) {
      finalParams = { token: userToken }
    }
  }

  // 执行刷新
  const refreshPromise = store
    .dispatch(refreshAction(finalParams))
    .unwrap()
    .then((result) => {
      console.log(`[refreshToken] ${axiosType} token 刷新成功`)
      return result
    })
    .catch((error) => {
      console.error(`[refreshToken] ${axiosType} token 刷新失败:`, error)
      throw error
    })
    .finally(() => {
      // 清除缓存
      tokenRefreshPromises[axiosType] = null
    })

  // 缓存 Promise
  tokenRefreshPromises[axiosType] = refreshPromise

  return refreshPromise
}

/**
 * 根据 axiosType 获取 API 格式配置
 * @param {string} axiosType - API 类型
 * @returns {Object} - API 格式配置
 */
const getApiFormatConfig = (axiosType) => {
  // 统一使用 axiosType 获取配置（与 api.js 保持一致）
  const mockConfig = {
    axiosType: axiosType
  }
  return getApiFormat(mockConfig) || API_FORMAT_CONFIG.default
}

/**
 * 根据 axiosType 和状态码判断是否需要刷新 token
 * @param {string} axiosType - API 类型
 * @param {number|string} statusCode - 响应状态码
 * @returns {boolean} - 是否需要刷新 token
 */
export const shouldRefreshToken = (axiosType, statusCode) => {
  try {
    const format = getApiFormatConfig(axiosType)
    if (!format) return false

    // 检查是否为登录失效码
    if (format.loginExpiredCodes && Array.isArray(format.loginExpiredCodes)) {
      return format.loginExpiredCodes.includes(statusCode)
    }
    if (format.loginExpiredCode !== null && format.loginExpiredCode !== undefined) {
      return statusCode === format.loginExpiredCode
    }
    return false
  } catch (e) {
    console.warn(`[refreshToken] 判断是否需要刷新 token 失败:`, e)
    return false
  }
}

/**
 * 统一处理登录失效：根据 axiosType 刷新对应的 token
 * @param {string} axiosType - API 类型
 * @param {number|string} statusCode - 响应状态码
 * @param {Object} options - 可选参数
 * @param {boolean} options.forceRefresh - 是否强制刷新（登录失效时应该强制刷新），默认 true
 * @returns {Promise} - 刷新 token 的 Promise
 */
export const handleLoginExpired = async (axiosType, statusCode, options = {}) => {
  // 检查是否需要刷新
  if (!shouldRefreshToken(axiosType, statusCode)) {
    return Promise.reject(new Error(`状态码 ${statusCode} 不是 ${axiosType} 的登录失效码`))
  }

  // javaJson / javaMultipart 与 java 共用 token 刷新逻辑
  const refreshAxiosType = (axiosType === 'javaJson' || axiosType === 'javaMultipart')
    ? 'java'
    : axiosType

  // 检查是否支持自动刷新
  const refreshAction = getTokenRefreshAction(refreshAxiosType)
  if (!refreshAction) {
    return Promise.reject(new Error(`${axiosType} 不支持自动刷新 token，需要跳转登录`))
  }

  // 登录失效时强制刷新（跳过过期时间检查）
  return refreshTokenByType(refreshAxiosType, {
    ...options,
    forceRefresh: true // 登录失效时强制刷新
  })
}
